// The site filter (AB#50180): the last site page the user opened, under the
// organization filter and behind its flag. Unlike the organization filter it
// never narrows a page's scope: pages highlight, focus or preselect the site.
// It can be paused or cleared on its own, and is kept across pages and reloads.
//
// Pure rules only, so they can be tested without React. AuthorizationContext
// wires them.

import { sameGuid } from "./orgFilter";

// --- Remembered filter ------------------------------------------------------

// Absent = no site filter. The site as the site page is opened with, plus the
// organization that owns it.
export const SITE_FILTER_STORAGE_KEY = "siteFilter";
export const SITE_FILTER_PAUSED_STORAGE_KEY = "siteFilterPaused";

// What is kept of a site: enough to open its page again and to tell which
// organization it belongs to.
export const toSiteFilter = (site, orgGuid = null) =>
  site?.siteGuid
    ? {
        siteGuid: site.siteGuid,
        orgGuid: orgGuid ?? site.customer?.customerGuid ?? null,
        name: site.name ?? "",
        city: site.city ?? null,
        country: site.country ?? null,
        latitude: site.latitude ?? null,
        longitude: site.longitude ?? null,
      }
    : null;

export const readStoredSiteFilter = () => {
  try {
    const raw = globalThis.localStorage?.getItem(SITE_FILTER_STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw);
    return stored?.siteGuid ? toSiteFilter(stored, stored.orgGuid) : null;
  } catch {
    return null;
  }
};

export const storeSiteFilter = (siteFilter) => {
  try {
    if (siteFilter) globalThis.localStorage?.setItem(SITE_FILTER_STORAGE_KEY, JSON.stringify(siteFilter));
    else globalThis.localStorage?.removeItem(SITE_FILTER_STORAGE_KEY);
  } catch {
    // Storage unavailable: the filter is just not remembered.
  }
};

export const readStoredSiteFilterPaused = () => {
  try {
    return globalThis.localStorage?.getItem(SITE_FILTER_PAUSED_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

export const storeSiteFilterPaused = (isPaused) => {
  try {
    if (isPaused) globalThis.localStorage?.setItem(SITE_FILTER_PAUSED_STORAGE_KEY, "1");
    else globalThis.localStorage?.removeItem(SITE_FILTER_PAUSED_STORAGE_KEY);
  } catch {
    // Storage unavailable: the pause is just not remembered.
  }
};

export const forgetStoredSiteFilter = () => {
  storeSiteFilter(null);
  storeSiteFilterPaused(false);
};

// --- Rules --------------------------------------------------------------------

// The site's own groups give the user the site: a role on the site or on its
// organization. A site seen only through a fleet is not one of them (the
// fleet's role does not reach the site group), and gets no site filter.
export const canFilterOnSite = ({ siteGuid, groups = [], isSuperAdmin = false }) => {
  if (!siteGuid) return false;
  if (isSuperAdmin) return true;
  const group = groups.find((g) => g.type === "Site" && sameGuid(g.guid, siteGuid));
  // Permissions.GroupRead, spelled out: AuthorizationContext imports this module.
  return !!group?.role?.permissions?.includes("group.read");
};

// The organization filter set to `orgGuid` (null: cleared) keeps a site
// filter only when the site belongs to that organization.
export const keepsSiteFilter = (siteFilter, orgGuid) =>
  !!siteFilter && !!orgGuid && sameGuid(siteFilter.orgGuid, orgGuid);

// A remembered site filter is restored only while the user still has the
// site, and, with an organization filter, when the site is in it.
export const restorableSiteFilter = ({ stored, groups, isSuperAdmin, filteredOrgGuid = null }) => {
  if (!stored) return null;
  if (!canFilterOnSite({ siteGuid: stored.siteGuid, groups, isSuperAdmin })) return null;
  if (filteredOrgGuid && !sameGuid(stored.orgGuid, filteredOrgGuid)) return null;
  return stored;
};

export const isFilteredSite = (siteGuid, filteredSite) =>
  !!filteredSite && sameGuid(siteGuid, filteredSite.siteGuid);
